import React, { Suspense, ReactNode, lazy } from "react";
import { Routes, Route, Navigate, NavigateFunction } from "react-router-dom";
import { observer, Provider } from "mobx-react";
import {
  Loader,
  withRouter,
  AuthService,
} from "@mindmemobile/mm-shared-global";
import {
  ItemsStore,
  RolesAndPermissionStore,
  rolesAndPermissionStore,
  ApplicationSettingsStore,
  PageNotFound,
  FindAccountRoutes,
  AdminRoutes,
  AdminAuthStore,
  PublicReceiptPage,
  // CountryStore,
  // AppRoutes,
} from "@mindmemobile/mm-payment-modules";
import {
  fundraiserStore,
  // FundraiserRoutes,
} from "@mindmemobile/mm-fundraiser-module";

class ItemStoreWrapper {
  public itemStore: ItemsStore;
  // public countryStore: CountryStore;
  public rolesAndPermissionStore: RolesAndPermissionStore;
  constructor() {
    this.itemStore = new ItemsStore();
    this.rolesAndPermissionStore = rolesAndPermissionStore;
    // this.countryStore = new CountryStore();
  }
}

type IProps = {
  fromMain?: boolean;
  navigate: NavigateFunction;
  location: Location;
};

type IState = {
  isFundraiser: boolean;
  isPaymentSystem: boolean;
};

// Lazy imports
const AppRoutes = lazy(() =>
  import("@mindmemobile/mm-payment-modules").then((m) => ({
    default: m.AppRoutes,
  }))
);

const FundraiserRoutes = lazy(() =>
  import("@mindmemobile/mm-fundraiser-module").then((m) => ({
    default: m.FundraiserRoutes,
  }))
);
// const itemStoreWrapper = new ItemStoreWrapper();
// Singleton reference
let itemStoreWrapper: ItemStoreWrapper | null = null;

export function getItemStoreWrapper(): ItemStoreWrapper {
  if (!itemStoreWrapper) {
    itemStoreWrapper = new ItemStoreWrapper();
  }
  return itemStoreWrapper;
}
class RoutesConfig extends React.Component<IProps, IState> {
  // The parent (Main_Web2) resends the same payment-code every second until it gets a reply,
  // to survive the iframe-load race. Each message re-runs navigate() + auth setup here, which
  // remounts FindAccount and re-fires its lookup - so we ignore exact-duplicate resends and
  // only act on the first (or a genuinely different) payload. A full-page reload creates a
  // fresh instance, so this never blocks a real sub-account switch.
  private lastPaymentCodeSignature: string | null = null;

  constructor(props: IProps) {
    super(props);
    this.state = {
      isFundraiser: false,
      isPaymentSystem: true,
    };
  }

  get fromPaymentSystem(): boolean {
    return this.props.fromMain === true;
  }

  componentDidMount(): void {
    // AuthService.setAuthData("65cb465ddb04cbbba41c9079", "7796", false, true);
    // AuthService.setMindmeAccountDetails({
    //   userId: "64e71d00916cc62b5162f7b9",
    //   subAccountId: "677bd8e37b0e5c4f1f8cfab4",
    //   parentAccountId: "64d33f95d2209a26171037b5",
    //   isNMIEnable: false,
    //   accountName: "Test Account",
    // });
    const data = this.redirectIfNeeded();
    if (data?.isFundraiser || data?.isPaymentSystem) {
      this.setState({
        isFundraiser: data.isFundraiser,
        isPaymentSystem: data.isPaymentSystem,
      });
      data?.isPaymentSystem &&
        AuthService.setFromPaymentSystem(this.fromPaymentSystem);
    }
    this.readIframeMessage();
    /* Ask the host for the payment-code now that the listener above is registered. The host cannot
       see when this app becomes ready, so it posts on its own schedule and retries blindly; asking
       removes that guesswork, and matters most when this frame replaces a previous one. */
    this.postToHost("payment-code-request");
  }

  /* Messages to the embedding app (Main_Web2). No-ops when this app runs standalone. */
  private postToHost(type: string) {
    if (!window.parent || window.parent === window) return;
    window.parent.postMessage(JSON.stringify({ type }), "*");
  }

  readIframeMessage() {
    window.addEventListener("message", (event: any) => {
      if (!event?.data || typeof event?.data !== "string") return;
      try {
        const data = JSON.parse(event?.data);

        if (data?.type === "payment-admin-key") {
          AdminAuthStore.setAdminApiKey(data.adminApiKey);
          this.props.navigate(data.navigateToRoute);
          return;
        }

        // Ignore Webpack messages
        if (data?.type !== "payment-code") return;

        /* Confirm receipt before anything else, including for a duplicate resend, so the host can
           stop retrying. Without this the host had to infer delivery from unrelated messages. */
        this.postToHost("payment-code-received");

        // Skip exact-duplicate resends from the parent's retry loop (see field comment).
        const paymentCodeSignature = JSON.stringify({
          accountId: data.accountId,
          navigateToRoute: data.navigateToRoute,
          isFundraiser: data.isFundraiser,
          subAccountId: data.mindmeAccountDetails?.subAccountId,
          parentAccountId: data.mindmeAccountDetails?.parentAccountId,
        });
        if (paymentCodeSignature === this.lastPaymentCodeSignature) return;
        this.lastPaymentCodeSignature = paymentCodeSignature;

        AuthService.setAuthData(
          data.accountId,
          AuthService.shortUniqueId, //keep from state
          data.isPrimarySubAccount,
          data.hasLibraryManagePermissions
        );
        AuthService.setFromPaymentSystem(false);

        AuthService.setMindMeRoute(data.navigateToRoute);

        if (data.mindmeAccountDetails) {
          AuthService.setMindmeAccountDetails(data.mindmeAccountDetails);
        }

        /* Apply the host's rights in BOTH modes. They used to be set only outside Fundraiser mode,
           so fundraiser actions ran on the store's defaults (security finding F02). */
        rolesAndPermissionStore.setPermissions(
          data.hasViewPermission === true,
          data.hasManagePermission === true,
          data.hasManageMerchantAccountPermission === true
        );

        const isFundraiser = data.isFundraiser === true;
        if (isFundraiser) {
          fundraiserStore.resetBuilder("");
        } else {
          AuthService.setHideLeftMenu(data.hideLeftMenu);
          AuthService.setHideHeaderBar(data.hideHeaderNav);
          const lastRoute =
            window.location.pathname !== data.navigateToRoute
              ? window.location.pathname
              : "";
          AuthService.setLastRoute(lastRoute);
          if (data.mindmeAccountDetails) {
            AuthService.setMindmeAccountDetails(data.mindmeAccountDetails);
          }
        }
        this.props.navigate(data.navigateToRoute);
        this.setState({
          isFundraiser: isFundraiser,
          isPaymentSystem: !isFundraiser,
        });
      } catch (e) {
        console.log("Error parsing data", e);
      }
    });
  }

  isPurchaseLink(): boolean {
    const isPurchaseLink =
      this.props.location.pathname.includes("app/purchase/checkout") ||
      this.props.location.pathname.includes("app/purchase/purchase-link");
    return isPurchaseLink;
  }

  /**
   * The public receipt page (/payment-receipt/:token), opened straight from a link in the
   * email. Like a purchase link it is a session-less public page, so it must skip both the
   * public-access restriction and the purchase-domain redirect - otherwise following the link lands
   * on page-not-found. The unguessable receipt token in the URL is what protects it.
   */
  isPublicReceipt(): boolean {
    return this.props.location.pathname.startsWith("/payment-receipt/");
  }

  redirectIfNeeded() {
    const { location } = this.props;
    let response = {
      isFundraiser: false,
      isPaymentSystem: false,
    };

    // const publicAccess = AuthService.mindmeAccountDetails === null
    const domain = window.location.hostname;
    // if (location.pathname.includes("/fundraiser")) {
    //   response.isFundraiser = true;

    //   if (
    //     !ApplicationSettingsStore.isFundRaiserDomainAllowed(domain) &&
    //     domain !== "localhost"
    //     && publicAccess
    //   ) {
    //     this.props.navigate("/page-not-found");
    //   }
    //   return response;
    // }

    const isPurchaseLink = this.isPurchaseLink() || this.isPublicReceipt();
    response.isPaymentSystem = isPurchaseLink;

    if (domain === "localhost") {
      return response;
    }
    const isDomainIsPurchaseAllowedDomain =
      ApplicationSettingsStore.isPurchaseDomainAllowed(domain);

    if (!isPurchaseLink) {
      if (isDomainIsPurchaseAllowedDomain) {
        this.props.navigate("/page-not-found");
        response.isPaymentSystem = true;
        return response;
      }
      return response;
    }

    const isAllowed =
      ApplicationSettingsStore.isPurchaseDomainAllowed(domain) ||
      ApplicationSettingsStore.isLoadingInProgress ||
      /* The allow-list is empty when the settings request failed, which is not the same as the
         domain being disallowed. Without this a settings outage would send paying customers on a
         valid purchase link to page-not-found. */
      ApplicationSettingsStore.loadFailed;
    if (!isAllowed) {
      console.log("isAllowed", isAllowed);
      console.log("window.location.pathname", window.location.pathname);
      console.log(
        "applicationSettings.purchaseDomains",
        ApplicationSettingsStore.applicationSettings.purchaseDomains
      );
      this.props.navigate("/page-not-found");
      response.isPaymentSystem = true;
      return response;
    }
    return response;
  }

  restrictPublicAccess() {
    const isPurchaseLink = this.isPurchaseLink();

    const isPurchasePreview =
      this.props.location.pathname.includes("/app/checkout/preview/detail") ||
      this.props.location.pathname.includes("/app/checkout/preview");

    const isPageNotFound =
      this.props.location.pathname.includes("page-not-found");

    if (
      isPurchaseLink ||
      isPageNotFound ||
      isPurchasePreview ||
      this.isPublicReceipt()
    ) {
      return false;
    }
    // const domain = window.location.hostname;
    // if (domain === "localhost") {
    //   return false;
    // }
    //hide for both staging and production
    const isLiveEnv =
      process.env.REACT_APP_IsLive === "true" ||
      process.env.REACT_APP_IsLive === "false";
    // const publicAccess = AuthService.mindmeAccountDetails === null

    // if (AuthService.fromPaymentSystem && isLiveEnv && !publicAccess && this.state.isFundraiser) {
    //   return true;
    // }

    if (AuthService.fromPaymentSystem && isLiveEnv) {
      return true;
    }

    return false;
  }

  render(): ReactNode {
    const { isFundraiser, isPaymentSystem } = this.state;

    const showLoader =
      (isPaymentSystem && ApplicationSettingsStore.isLoadingInProgress) ||
      (!isFundraiser && !isPaymentSystem);
    if (showLoader) {
      return <>{new Loader().loaderComponent}</>;
    }

    this.redirectIfNeeded();

    const restrictPublicAccess = this.restrictPublicAccess();

    if (restrictPublicAccess) {
      return <></>;
    }

    console.log("isPaymentSystem", isPaymentSystem);
    console.log("window.location.pathname", window.location.pathname);
    return (
      <Suspense fallback={<>{new Loader().loaderComponent}</>}>
        <Routes>
          {isPaymentSystem && (
            <Route path="/" element={<Navigate to="/app/activity" />} />
          )}
          <Route
            path="/fundraiser/*"
            element={
              <Suspense fallback={<div>Loading Fundraiser...</div>}>
                <FundraiserRoutes />
              </Suspense>
            }
          />
          {/* Public receipt page - the "Download PDF" target in the receipt email. Declared
              before the catch-all app route so it never gets swallowed by it, and needs no auth. */}
          <Route path="/payment-receipt/:token" element={<PublicReceiptPage />} />
          <Route path={`/app/find-account/*`} element={<FindAccountRoutes />} />
          <Route path={`/app/admin/*`} element={<AdminRoutes />} />
          <Route
            path={this.fromPaymentSystem ? `/app/*` : "/*"}
            element={
              <Provider {...getItemStoreWrapper()}>
                <Suspense fallback={<div>Loading App...</div>}>
                  <AppRoutes />
                </Suspense>
              </Provider>
            }
          />
          <Route path="/page-not-found" element={<PageNotFound />} />
          <Route path="*" element={<Navigate to="/page-not-found" />} />
        </Routes>
      </Suspense>
    );
  }
}
export default withRouter(observer(RoutesConfig));
